import { useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router';

import { useAxios } from '@/hooks/useAxios';
import { useAuth } from '@/state/auth';
import { useMutation } from '@/utils/api';

const FEATUREBASE_BASE_URL = 'https://praetorian.featurebase.app';
const FEATUREBASE_DOCS_URL = 'https://docs.praetorian.com';
const FEATUREBASE_PORTAL_URL = 'https://portal.praetorian.com';

function isValidFeaturebaseReturnUrl(url: string | null): url is string {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    const allowedOrigins = [
      new URL(FEATUREBASE_BASE_URL).origin,
      new URL(FEATUREBASE_DOCS_URL).origin,
      new URL(FEATUREBASE_PORTAL_URL).origin,
    ];
    return allowedOrigins.includes(parsed.origin);
  } catch {
    return false;
  }
}

/**
 * FeaturebaseSSO handles SSO redirect from Featurebase to Chariot and back.
 *
 * Flow:
 * 1. User visits praetorian.featurebase.app without Chariot auth
 * 2. Featurebase redirects to /featurebase-sso?return_to=<url>
 * 3. If not logged in: render <Navigate> to /login with preserved return_to
 * 4. If logged in: POST /broker for JWT, redirect to Featurebase with JWT
 */
export function FeaturebaseSSO() {
  const { isSignedIn } = useAuth();
  const axios = useAxios();
  const [searchParams] = useSearchParams();

  const returnTo = searchParams.get('return_to');

  const brokerMutation = useMutation({
    defaultErrorMessage: 'Failed to authenticate with Featurebase',
    mutationFn: async () => {
      const response = await axios.post('/broker', {
        credentialId: 'featurebase',
        type: 'featurebase',
        category: 'internal',
        operation: 'get',
        format: ['token'],
        resolution: 'global',
      });
      return response;
    },
    onSuccess: (response: {
      data?: { credentialValue?: { featurebase?: string } };
    }) => {
      const jwt = response.data?.credentialValue?.featurebase;
      if (!jwt) return;

      const ssoUrl = new URL(`${FEATUREBASE_BASE_URL}/api/v1/auth/access/jwt`);
      ssoUrl.searchParams.set('jwt', jwt);
      if (isValidFeaturebaseReturnUrl(returnTo)) {
        ssoUrl.searchParams.set('return_to', returnTo);
      }
      window.location.href = ssoUrl.toString();
    },
  });

  useEffect(() => {
    if (isSignedIn) {
      brokerMutation.mutate();
    }
    // brokerMutation.mutate is stable from useMutation; isSignedIn is the only trigger
  }, [isSignedIn]);

  // Declarative redirect for unauthenticated users (after all hooks)
  if (!isSignedIn) {
    const currentPath = window.location.pathname + window.location.search;
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(currentPath)}`}
        replace
      />
    );
  }

  if (brokerMutation.isError) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-chariot-status-critical">
          Failed to authenticate with Featurebase.
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="text-chariot-text-muted">
        Redirecting to Featurebase...
      </div>
    </div>
  );
}
